<template>
  <div class="map-container">
    <div class="map-content">
      <l-map 
        ref="leafletMap"
        style="height: 100%;" 
        :zoom="zoom" 
        :center="center" 
        :options="mapOptions" 
        @update:center="centerUpdated" 
        @update:zoom="zoomUpdated" 
        @ready="onMapReady"
        @mousemove="onMouseMove">
        <l-tile-layer 
          v-for="layer in layers"
          :key="layer.name"
          :url="layer.url"
          :attribution="layer.attribution"
          :subdomains="layer.subdomains"
          :layer-type="layer.layerType"
          :visible="layer.visible"
          :options="layer.options"
        />
        
        <!-- 使用SVG标记直接添加船舶 -->
        <l-marker 
          v-for="ship in ships.slice(0, 30)" 
          v-if="ship.pos" 
          :key="ship.userid"
          :lat-lng="[ship.pos.latitude, ship.pos.longitude]"
          @click="showShipInfo(ship)"
        >
          <l-icon
            :icon-url="getShipIconUrl(ship)"
            :icon-size="[24, 24]"
            :icon-anchor="[12, 12]"
            :class-name="`ship-icon ship-${ship.userid}`"
          />
        </l-marker>
        
        <!-- 直接在地图内添加比例尺控件 -->
        <l-control-scale position="bottomleft" :imperial="false" :metric="true"></l-control-scale>
      </l-map>

      <div class="map-type-buttons">
        <el-tooltip content="矢量地图" placement="left">
          <div class="map-type-icon" 
               :class="{ active: currentMapType === 'normal' }" 
               @click="changeMapType('normal')">
            <i class="el-icon-map-location"></i>
          </div>
        </el-tooltip>
        <el-tooltip content="卫星影像" placement="left">
          <div class="map-type-icon" 
               :class="{ active: currentMapType === 'satellite' }" 
               @click="changeMapType('satellite')">
            <i class="el-icon-picture-outline"></i>
          </div>
        </el-tooltip>
        <el-tooltip content="地形图" placement="left">
          <div class="map-type-icon" 
               :class="{ active: currentMapType === 'terrain' }" 
               @click="changeMapType('terrain')">
            <i class="el-icon-data-analysis"></i>
          </div>
        </el-tooltip>
      </div>
      
      <div class="map-controls-wrapper">
        <!-- 仅显示经纬度部分 -->
        <div class="coordinate-panel">
          <div class="coordinate-line">经度 {{ formatLongitude }}</div>
          <div class="coordinate-line">纬度 {{ formatLatitude }}</div>
        </div>
      </div>
    </div>
    
    <!-- 船舶信息对话框 -->
    <el-dialog
      title="船舶信息"
      :visible.sync="shipDialogVisible"
      width="30%">
      <div v-if="currentShip" class="ship-popup">
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="MMSI">{{ currentShip.userid }}</el-descriptions-item>
          <el-descriptions-item label="消息类型">{{ currentShip.msgid }}</el-descriptions-item>
          <el-descriptions-item v-if="currentShip.pos" label="经度">{{ currentShip.pos.longitude }}</el-descriptions-item>
          <el-descriptions-item v-if="currentShip.pos" label="纬度">{{ currentShip.pos.latitude }}</el-descriptions-item>
          <el-descriptions-item v-if="currentShip.cog !== undefined" label="航向">{{ formatCog(currentShip.cog) }}°</el-descriptions-item>
          <el-descriptions-item v-if="currentShip.sog !== undefined" label="航速">{{ formatSog(currentShip.sog) }} 节</el-descriptions-item>
          <el-descriptions-item label="接收时间">{{ currentShip.receive_time }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
    
    <el-drawer
      title="船舶列表"
      :visible.sync="drawerVisible"
      direction="rtl"
      size="30%">
      <div class="ship-list">
        <el-table
          :data="ships"
          style="width: 100%"
          height="calc(100vh - 150px)"
          @row-click="locateShip">
          <el-table-column
            prop="userid"
            label="MMSI"
            width="120">
          </el-table-column>
          <el-table-column
            prop="msgid"
            label="消息类型"
            width="100">
          </el-table-column>
          <el-table-column
            label="位置"
            width="180">
            <template slot-scope="scope">
              <span v-if="scope.row.pos">
                {{ scope.row.pos.longitude.toFixed(4) }}, {{ scope.row.pos.latitude.toFixed(4) }}
              </span>
              <span v-else>--</span>
            </template>
          </el-table-column>
          <el-table-column
            label="接收时间">
            <template slot-scope="scope">
              {{ scope.row.receive_time }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-drawer>
    
    <div class="map-footer">
      <el-button type="primary" icon="el-icon-menu" circle @click="drawerVisible = true"></el-button>
    </div>
  </div>
</template>

<script>
import { LMap, LTileLayer, LControlScale, LMarker, LIcon } from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import '@/utils/chinaProvider' // 导入天地图插件
import shipData from '@/mockdata/data.json'
import L from 'leaflet'

export default {
  name: 'ShipMap_origin',
  components: {
    LMap,
    LTileLayer,
    LControlScale,
    LMarker,
    LIcon
  },
  data() {
    // 从URL获取地图类型
    const urlType = this.$route && this.$route.query.type;
    const validTypes = ['normal', 'satellite', 'terrain'];
    const initialMapType = validTypes.includes(urlType) ? urlType : 'normal';
    
    return {
      // 船舶数据
      ships: [],
      // 当前选中的船舶
      currentShip: null,
      // 船舶信息对话框可见性
      shipDialogVisible: false,
      // 地图中心点
      center: [30.4655, 121.0110],
      // 缩放级别
      zoom: 12,
      // 当前地图类型
      currentMapType: initialMapType,
      // 地图类型选项
      mapTypes: [
        { label: '矢量地图', value: 'normal' },
        { label: '卫星影像', value: 'satellite' },
        { label: '地形图', value: 'terrain' }
      ],
      // 天地图key
      tiandituKey: '038937b9b91fd28203af58e3dd9b1396',
      // 图层配置
      layers: [],
      // 地图配置选项
      mapOptions: {
        zoomControl: false, // 禁用默认缩放控件，用ElementUI的slider代替
        attributionControl: true
      },
      // 抽屉可见性
      drawerVisible: false,
      // 是否显示标注
      showLabels: true,
      // Leaflet地图实例
      map: null,
      // 鼠标位置
      mousePosition: {
        lat: 0,
        lng: 0
      },
      // 用于存储上次打印日志的时间
      _lastLogTime: null,
      // 船舶图标的SVG字符串
      shipIconSVG: `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path fill="#3a5eea" stroke="#1c2f7b" stroke-width="1" d="M12 2 L18 18 L12 14 L6 18 Z"/>
        </svg>
      `
    }
  },
  computed: {
    // 格式化经度显示
    formatLongitude() {
      return this.convertToDMS(this.mousePosition.lng, 'lng');
    },
    // 格式化纬度显示
    formatLatitude() {
      return this.convertToDMS(this.mousePosition.lat, 'lat');
    }
  },
  watch: {
    // 监听showLabels变化，动态控制标注图层
    showLabels(newVal) {
      this.toggleLabels(newVal);
    },
    // 监听路由变化
    '$route.query.type'(newType) {
      if (newType && ['normal', 'satellite', 'terrain'].includes(newType)) {
        this.changeMapType(newType);
      }
    },
    // 监听地图对象变化
    map(newVal) {
      if (newVal) {
        console.log('地图对象已初始化');
      } else {
        console.warn('地图对象为空或已销毁');
      }
    }
  },
  created() {
    console.log('ShipMap组件已创建');
    // 初始化图层
    this.initLayers();
    // 加载船舶数据
    this.loadShipData();
    // 创建船舶图标缓存
    this.createShipIcons();
  },
  mounted() {
    console.log('ShipMap组件已挂载');
    // 初始化地图实例
    this.$nextTick(() => {
      console.log('ShipMap nextTick检查');
      this.initMapInstance();
      
      // 确保鼠标事件被正确添加
      setTimeout(() => {
        if (this.map) {
          console.log('再次确认地图事件绑定');
          // 先移除可能重复的事件监听
          this.map.off('mousemove', this.onMouseMove);
          // 重新添加事件监听
          this.map.on('mousemove', this.onMouseMove);
        }
      }, 500);
      
      if (this.ships.length === 0) {
        console.log('重新加载船舶数据');
        this.loadShipData();
      } else {
        console.log(`船舶数据已加载 (${this.ships.length}条)`);
      }
    });
  },
  methods: {
    // 初始化地图实例
    initMapInstance() {
      const leafletRef = this.$refs.leafletMap;
      if (leafletRef && leafletRef.mapObject) {
        console.log('通过$refs初始化地图实例');
        this.map = leafletRef.mapObject;
        
        // 直接绑定鼠标移动事件
        this.map.off('mousemove', this.onMouseMove);
        this.map.on('mousemove', this.onMouseMove);
      }
    },
    
    // 创建船舶图标
    createShipIcons() {
      // 创建一个基本的SVG图标
      this.shipIconSVG = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path fill="#3a5eea" stroke="#1c2f7b" stroke-width="1" d="M12 2 L18 18 L12 14 L6 18 Z"/>
        </svg>
      `;
    },
    
    // 获取船舶图标URL
    getShipIconUrl(ship) {
      // 根据航向旋转图标
      const heading = ship.cog !== undefined ? (ship.cog / 10) : 0;
      
      // 创建带有旋转的SVG
      const rotatedSVG = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <g transform="rotate(${heading}, 12, 12)">
            <path fill="#3a5eea" stroke="#1c2f7b" stroke-width="1" d="M12 2 L18 18 L12 14 L6 18 Z"/>
          </g>
        </svg>
      `;
      
      // 转换为Data URL
      return 'data:image/svg+xml;base64,' + btoa(rotatedSVG);
    },
    
    // 加载船舶数据
    loadShipData() {
      console.log('加载船舶数据', shipData);
      if (!shipData || !Array.isArray(shipData)) {
        console.error('船舶数据无效:', shipData);
        return;
      }
      
      this.ships = shipData;
      
      // 计算地图中心点（取所有有位置的船舶的平均位置）
      const shipsWithPos = this.ships.filter(ship => ship && ship.pos);
      console.log('有位置的船舶数量:', shipsWithPos.length);
      if (shipsWithPos.length > 0) {
        const sumLat = shipsWithPos.reduce((sum, ship) => sum + ship.pos.latitude, 0);
        const sumLng = shipsWithPos.reduce((sum, ship) => sum + ship.pos.longitude, 0);
        this.center = [sumLat / shipsWithPos.length, sumLng / shipsWithPos.length];
        console.log('地图中心点:', this.center);
      }
    },
    // 获取船舶位置
    getShipLatLng(ship) {
      if (ship.pos) {
        return [ship.pos.latitude, ship.pos.longitude];
      }
      // 如果没有位置信息，返回地图中心点
      return this.center;
    },
    // 显示船舶信息
    showShipInfo(ship) {
      this.currentShip = ship;
      this.shipDialogVisible = true;
    },
    // 格式化航向
    formatCog(cog) {
      return (cog / 10).toFixed(1);
    },
    // 格式化航速
    formatSog(sog) {
      return (sog / 10).toFixed(1);
    },
    // 定位到船舶
    locateShip(row) {
      if (row.pos) {
        this.center = [row.pos.latitude, row.pos.longitude];
        this.zoom = 14;
      }
      this.drawerVisible = false;
    },
    // 初始化图层
    initLayers() {
      this.updateLayers(this.currentMapType)
    },
    // 更新图层
    updateLayers(mapType) {
      // 清空当前图层
      this.layers = []
      
      // 配置图层
      switch(mapType) {
        case 'normal':
          // 矢量图层
          this.layers.push({
            name: 'TianDiTu.Normal.Map',
            url: `https://t{s}.tianditu.gov.cn/DataServer?T=vec_w&X={x}&Y={y}&L={z}&tk=${this.tiandituKey}`,
            attribution: '&copy; 天地图',
            subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
            layerType: 'base',
            visible: true,
            options: {
              maxZoom: 18,
              minZoom: 5
            }
          })
          // 注记图层
          if (this.showLabels) {
            this.layers.push({
              name: 'TianDiTu.Normal.Annotion',
              url: `https://t{s}.tianditu.gov.cn/DataServer?T=cva_w&X={x}&Y={y}&L={z}&tk=${this.tiandituKey}`,
              attribution: '&copy; 天地图',
              subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
              layerType: 'overlay',
              visible: true,
              options: {
                maxZoom: 18,
                minZoom: 5
              }
            })
          }
          break
        case 'satellite':
          // 卫星影像
          this.layers.push({
            name: 'TianDiTu.Satellite.Map',
            url: `https://t{s}.tianditu.gov.cn/DataServer?T=img_w&X={x}&Y={y}&L={z}&tk=${this.tiandituKey}`,
            attribution: '&copy; 天地图',
            subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
            layerType: 'base',
            visible: true,
            options: {
              maxZoom: 18,
              minZoom: 5
            }
          })
          // 卫星影像注记
          if (this.showLabels) {
            this.layers.push({
              name: 'TianDiTu.Satellite.Annotion',
              url: `https://t{s}.tianditu.gov.cn/DataServer?T=cia_w&X={x}&Y={y}&L={z}&tk=${this.tiandituKey}`,
              attribution: '&copy; 天地图',
              subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
              layerType: 'overlay',
              visible: true,
              options: {
                maxZoom: 18,
                minZoom: 5
              }
            })
          }
          break
        case 'terrain':
          // 地形图
          this.layers.push({
            name: 'TianDiTu.Terrain.Map',
            url: `https://t{s}.tianditu.gov.cn/DataServer?T=ter_w&X={x}&Y={y}&L={z}&tk=${this.tiandituKey}`,
            attribution: '&copy; 天地图',
            subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
            layerType: 'base',
            visible: true,
            options: {
              maxZoom: 18,
              minZoom: 5
            }
          })
          // 地形图注记
          if (this.showLabels) {
            this.layers.push({
              name: 'TianDiTu.Terrain.Annotion',
              url: `https://t{s}.tianditu.gov.cn/DataServer?T=cta_w&X={x}&Y={y}&L={z}&tk=${this.tiandituKey}`,
              attribution: '&copy; 天地图',
              subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
              layerType: 'overlay',
              visible: true,
              options: {
                maxZoom: 18,
                minZoom: 5
              }
            })
          }
          break
      }
    },
    // 切换地图类型
    changeMapType(type) {
      this.currentMapType = type
      this.updateLayers(type)
    },
    // 地图准备完成
    onMapReady(event) {
      console.log('地图ready事件触发', event);
      
      // vue2-leaflet的ready事件在某些情况下可能传递不规范的事件对象
      // 这里进行兼容性处理
      if (!event || typeof event !== 'object') {
        console.error('地图初始化失败：事件对象为空');
        this.initMapInstance();
        return;
      }
      
      // event可能是直接的地图对象或包含target的事件对象
      if (event.getContainer) {
        console.log('事件对象是地图实例');
        this.map = event;
      } else if (event.target && event.target.getContainer) {
        console.log('事件对象包含地图实例');
        this.map = event.target;
      } else {
        console.error('地图初始化失败：无效的事件对象');
        this.initMapInstance();
        return;
      }
      
      // 添加鼠标移动事件监听
      if (this.map) {
        this.map.on('mousemove', this.onMouseMove);
        console.log('地图初始化完成');
      }
    },
    // 鼠标移动事件
    onMouseMove(e) {
      // 确保更新mousePosition
      if (e && e.latlng) {
        // 使用Vue的响应式特性，确保更新被检测到
        this.$set(this, 'mousePosition', {
          lat: e.latlng.lat,
          lng: e.latlng.lng
        });
        
        // 测试用：每10秒打印一次，确认事件正在触发
        const now = Date.now();
        if (!this._lastLogTime || now - this._lastLogTime > 10000) {
          console.log('经纬度更新:', e.latlng.lat.toFixed(6), e.latlng.lng.toFixed(6));
          this._lastLogTime = now;
        }
      }
    },
    // 中心点更新
    centerUpdated(center) {
      this.center = center;
    },
    // 缩放级别更新
    zoomUpdated(zoom) {
      this.zoom = zoom;
    },
    // 经纬度转换为度分秒格式
    convertToDMS(decimal, type) {
      const absolute = Math.abs(decimal);
      const degrees = Math.floor(absolute);
      const minutesNotTruncated = (absolute - degrees) * 60;
      const minutes = Math.floor(minutesNotTruncated);
      const seconds = ((minutesNotTruncated - minutes) * 60).toFixed(2);
      
      const direction = type === 'lat' 
        ? (decimal >= 0 ? 'N' : 'S') 
        : (decimal >= 0 ? 'E' : 'W');
      
      return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
    },
    // 切换标注
    toggleLabels(show) {
      this.updateLayers(this.currentMapType)
    }
  }
}
</script>

<style scoped>
.map-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
}

.map-content {
  flex: 1;
  position: relative;
}

/* 确保地图能接收事件 */
.map-content >>> .leaflet-map-pane {
  z-index: 200;
  pointer-events: auto;
}

/* 确保地图控件在船舶图层上方 */
.map-content >>> .leaflet-control-container {
  z-index: 600;
  pointer-events: auto;
}

.map-footer {
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

.map-controls-wrapper {
  position: absolute;
  bottom: 25px;
  left: 15px;
  z-index: 1000;
}

.coordinate-panel {
  background-color: #fff;
  border-radius: 4px;
  padding: 6px 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  min-width: 120px;
  margin-bottom: 5px;
  border: 1px solid #e0e0e0;
}

.coordinate-line {
  color: #222;
  font-size: 12px;
  font-family: 'Consolas', monospace;
  text-align: left;
  margin: 2px 0;
  white-space: nowrap;
}

/* 添加浮动地图类型图标样式 */
.map-type-buttons {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.map-type-icon {
  width: 32px;
  height: 32px;
  background-color: rgba(28, 47, 123, 0.85);
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #e3eafc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.3s;
}

.map-type-icon:hover {
  background-color: #3a5eea;
  color: white;
}

.map-type-icon.active {
  background-color: #3a5eea;
  color: white;
}

.map-type-icon i {
  font-size: 16px;
}

.ship-popup {
  min-width: 200px;
}

.ship-list {
  padding: 10px;
}

.ship-icon {
  background: transparent !important;
}
</style>

<style>
/* 覆盖Leaflet比例尺样式，使其与我们的主题一致 */
.leaflet-control-scale-line {
  border-color: #e0e0e0 !important;
  background-color: #fff !important;
  color: #222 !important;
  margin-left: 10px !important;
  margin-bottom: 5px !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
}

/* 确保地图能接收事件 */
.leaflet-container {
  z-index: 100 !important;
  pointer-events: auto !important;
}

/* 确保地图瓦片能接收事件 */
.leaflet-tile-container {
  pointer-events: auto !important;
}

/* 确保地图交互元素在正确的层级 */
.leaflet-pane {
  z-index: 300 !important;
}

.leaflet-tile-pane {
  z-index: 200 !important;
}

.leaflet-overlay-pane {
  z-index: 400 !important;
}

.leaflet-control {
  z-index: 800 !important;
}

.leaflet-top, .leaflet-bottom {
  z-index: 1000 !important;
}
</style> 